"use client";

import {
  MouseEvent as UIMouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@drpcrd/utils/shared";
import { Menu, X } from "lucide-react";
import { SidebarContext, SidebarSlot, useSidebarContext } from "./sidebar-context";

type Props = {
  /** Navigation content — compose from `SidebarHeader`, `SidebarBody`, and
   * `SidebarFooter`, e.g.:
   * ```tsx
   * <Sidebar isExpandable>
   *   <SidebarHeader logo={<Logo />} title="Nutcake" />
   *   <SidebarBody>
   *     <SidebarNavItem title="Home" icon={House} href="/" />
   *   </SidebarBody>
   *   <SidebarFooter>
   *     <SidebarAccountMenu ... />
   *   </SidebarFooter>
   * </Sidebar>
   * ```
   * Each child is mounted into all three responsive contexts (the mobile
   * fixed bar, the mobile slide-over menu, and the desktop rail) and adapts
   * its own rendering via `useSidebarContext()` rather than the parent
   * cloning/extracting specific children — the same pattern as this
   * package's Radix-based `Tabs`/`Dialog` compound components.
   */
  children: ReactNode;
  /** Whether the desktop rail can be collapsed to an icon-only rail and
   * resized. Non-expandable sidebars (e.g. auth) are always fully expanded. */
  isExpandable?: boolean;
  className?: string;
  /** Controlled expanded state — omit to let the sidebar manage its own. */
  isExpanded?: boolean;
  /** Called with the next expanded state whenever the sidebar is toggled. */
  onToggle?: (isExpanded: boolean) => void;
};

const MIN_SIDEBAR_WIDTH = 240;
const MAX_SIDEBAR_WIDTH = 480;
const COLLAPSED_SIDEBAR_WIDTH = 64;
const DEFAULT_SIDEBAR_WIDTH = 320;

/**
 * Collapsible, resizable dashboard sidebar shell. Compose it from
 * `SidebarHeader`/`SidebarBody`/`SidebarFooter` children — see the `Props`
 * doc comment for the shape. Handles expand/collapse + resize on desktop
 * and a fixed top bar + slide-over menu on mobile; the compound children
 * read `useSidebarContext()` to adapt to whichever of the three they're
 * currently rendered into.
 */
export const Sidebar = ({
  children,
  isExpandable = false,
  className,
  isExpanded: controlledIsExpanded,
  onToggle,
}: Props) => {
  const isControlled = controlledIsExpanded !== undefined;
  const [localIsExpanded, setLocalIsExpanded] = useState(isExpandable);
  const isExpanded = !isExpandable || (isControlled ? controlledIsExpanded : localIsExpanded);

  const [width, setWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showContent, setShowContent] = useState(!isExpandable || isExpanded);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const toggleSidebar = () => {
    if (!isExpandable) return;
    const next = !isExpanded;
    if (isControlled) {
      onToggle?.(next);
    } else {
      setLocalIsExpanded(next);
      onToggle?.(next);
    }
  };

  useEffect(() => {
    if (!isExpandable) return;
    if (isExpanded) {
      setWidth(DEFAULT_SIDEBAR_WIDTH);
      const timeout = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timeout);
    }
    setShowContent(false);
  }, [isExpanded, isExpandable]);

  const handleMouseDown = (e: UIMouseEvent) => {
    if (!isExpanded || !isExpandable) return;
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, startWidth + deltaX));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const closeMobileMenu = () => {
    document.body.style.overflow = "unset";
    setIsMobileMenuOpen(false);
  };
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => {
      document.body.style.overflow = prev ? "unset" : "hidden";
      return !prev;
    });
  };

  const currentWidth = isExpanded ? width : COLLAPSED_SIDEBAR_WIDTH;

  const contextFor = (slot: SidebarSlot) => ({
    slot,
    isExpandable,
    isExpanded: slot === "desktop" ? isExpanded : true,
    toggleSidebar,
    isMobileMenuOpen,
    closeMobileMenu,
    showContent: slot === "desktop" ? showContent : true,
  });

  return (
    <>
      {/* Mobile fixed top bar */}
      <div className="fixed top-0 right-0 left-0 z-40 flex h-8 items-center justify-between border-b border-neutral-200 bg-white p-2 lg:hidden">
        <SidebarContext.Provider value={contextFor("mobile-bar")}>
          {children}
        </SidebarContext.Provider>
        <button aria-label="Open mobile menu" onClick={toggleMobileMenu}>
          <Menu className="text-neutral-900 hover:cursor-pointer hover:text-black" />
        </button>
      </div>

      {/* Mobile slide-over menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="bg-opacity-50 fixed inset-0 bg-black" onClick={closeMobileMenu} />
          <div
            className="fixed inset-y-0 right-0 left-0 flex flex-col bg-white shadow-xl"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <SidebarContext.Provider value={contextFor("mobile-menu")}>
              {children}
            </SidebarContext.Provider>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={cn("hidden lg:flex", className)}>
        <aside
          ref={sidebarRef}
          className={cn(
            "relative flex shrink-0 flex-col border-r border-r-neutral-500 bg-white transition-all duration-300 ease-in-out",
            isResizing && "transition-none",
          )}
          style={{ width: `${currentWidth}px` }}
        >
          <SidebarContext.Provider value={contextFor("desktop")}>
            {children}
          </SidebarContext.Provider>

          {isExpanded && isExpandable && (
            <div
              className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent"
              onMouseDown={handleMouseDown}
              style={{ right: "-2px" }}
            />
          )}
        </aside>
      </div>
    </>
  );
};

/** Close button for the mobile slide-over menu — used inside `SidebarHeader`
 * when `slot === "mobile-menu"`. Exported so a consumer with a fully custom
 * header can still trigger it. */
export const SidebarMobileMenuClose = () => {
  const { closeMobileMenu } = useSidebarContext();
  return (
    <button onClick={closeMobileMenu} aria-label="Close mobile menu">
      <X className="text-neutral-900 hover:cursor-pointer hover:text-black" />
    </button>
  );
};
